/**
 * Setup: the wine lookup lists — producers, regions & communes, vineyards,
 * varietals — behind the RSVP wine form (route `/setup`, `lookups:manage`).
 *
 * Members add what's missing as they RSVP ("Not in the list? Add it"); it lands
 * here flagged for review. Admins fix the spelling in place, place it in the
 * right region, merge a duplicate into the entry that should survive, tick a
 * batch as "Looks right", or delete something nothing uses. A rename or merge
 * follows through to the Cellar, RSVPs and tasting pours on the server.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WINE_LOOKUP_KINDS,
  WINE_LOOKUP_LABELS,
  type LookupAdminRow,
  type WineLookupKind,
} from '@gwc/shared';
import { lookupAdminApi } from '../api/lookupAdmin';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { ApiError } from '../lib/api';
import { toast } from '../lib/toast';
import { Pagination } from '../components/Pagination';
import { PageSkeleton } from '../components/Skeleton';
import { Button, useConfirm } from '../components/ui';
import { LookupRow } from '../components/setup/LookupRow';
import { LookupEditDialog } from '../components/setup/LookupEditDialog';
import { MergeDialog } from '../components/setup/MergeDialog';
import { draftToPatch, type LookupDraft, type PickedLookup } from '../components/setup/setupHelpers';

const PAGE_SIZE = 50;

const isKind = (k: string | null): k is WineLookupKind =>
  !!k && (WINE_LOOKUP_KINDS as readonly string[]).includes(k);

const errorMessage = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

export function Setup() {
  const [params, setParams] = useSearchParams();
  const listParam = params.get('list');
  const kind: WineLookupKind = isKind(listParam) ? listParam : 'producers';
  const [q, setQ] = useState('');
  const [needsReview, setNeedsReview] = useState(true);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<LookupAdminRow | 'new' | null>(null);
  const [merging, setMerging] = useState<{ row: LookupAdminRow; into: PickedLookup | null } | null>(null);
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const qc = useQueryClient();
  const confirm = useConfirm();

  const labels = WINE_LOOKUP_LABELS[kind];
  const one = labels.one.toLowerCase();
  const many = labels.many.toLowerCase();

  function switchKind(k: WineLookupKind) {
    setParams(k === 'producers' ? {} : { list: k });
    setQ('');
    setPage(1);
    setSelected(new Set());
  }

  const listQuery = useQuery({
    queryKey: ['lookupAdmin', kind, 'manage', { q: debouncedQ, needsReview, page }],
    queryFn: () =>
      lookupAdminApi.list(kind, {
        q: debouncedQ || undefined,
        needsReview: needsReview || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });
  const rows = listQuery.data?.data ?? [];

  const reviewCounts = useQueries({
    queries: WINE_LOOKUP_KINDS.map((k) => ({
      queryKey: ['lookupAdmin', k, 'reviewCount'],
      queryFn: () => lookupAdminApi.list(k, { needsReview: true, pageSize: 1 }),
      staleTime: 30_000,
    })),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['lookupAdmin'] });

  const review = useMutation({
    mutationFn: (ids: string[]) => lookupAdminApi.review(kind, ids),
    onSuccess: ({ updated }) => {
      toast.success(updated === 1 ? 'Marked as checked' : `${updated} marked as checked`);
      setSelected(new Set());
      void refresh();
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not mark them as checked')),
  });

  const seed = useMutation({
    mutationFn: () => lookupAdminApi.seed(),
    onSuccess: (r) => {
      toast.success(
        r.regions || r.varietals
          ? `Added ${r.regions} region${r.regions === 1 ? '' : 's'} and ${r.varietals} grape${r.varietals === 1 ? '' : 's'}`
          : 'The default regions and grapes are all there',
      );
      void refresh();
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not add the defaults')),
  });

  /** A rename can make two Cellar wines identical; the server flags them instead. */
  function reportConflicts(conflicts: string[] | undefined) {
    if (!conflicts?.length) return;
    toast.error(
      conflicts.length === 1
        ? '1 Cellar wine now matches another entry exactly — it was flagged for review in the Cellar.'
        : `${conflicts.length} Cellar wines now match other entries exactly — they were flagged for review in the Cellar.`,
    );
  }

  async function rename(row: LookupAdminRow, name: string) {
    const res = await lookupAdminApi.update(kind, row.id, { name });
    toast.success(`Renamed to ${name}`);
    reportConflicts(res.catalogConflicts);
    void refresh();
  }

  async function remove(row: LookupAdminRow) {
    const ok = await confirm({
      title: `Delete ${row.name}?`,
      description: `Nothing uses this ${one}, so it can go. It disappears from the wine form.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await lookupAdminApi.remove(kind, row.id);
      toast.success(`Deleted ${row.name}`);
      setSelected((s) => {
        const next = new Set(s);
        next.delete(row.id);
        return next;
      });
      void refresh();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not delete it'));
    }
  }

  async function saveDraft(d: LookupDraft) {
    if (editing === 'new') {
      const parentId = d.parent?.id ?? null;
      const res = await lookupAdminApi.add(kind, {
        name: d.name.trim(),
        ...(kind === 'regions' ? { world: d.world, parentId } : {}),
        ...(kind === 'vineyards' || kind === 'producers' ? { regionId: parentId } : {}),
        ...(kind === 'varietals' ? { color: d.color } : {}),
      });
      toast.success(res.created ? `Added ${res.data.name}` : `${res.data.name} is already in the list`);
      // Region kind isn't part of "add"; set it straight after when chosen.
      if (res.created && kind === 'regions' && d.regionKind) {
        await lookupAdminApi.update(kind, res.data.id, { regionKind: d.regionKind });
      }
    } else if (editing) {
      const patch = draftToPatch(kind, editing, d);
      if (Object.keys(patch).length) {
        const res = await lookupAdminApi.update(kind, editing.id, patch);
        toast.success('Saved');
        reportConflicts(res.catalogConflicts);
      }
    }
    setEditing(null);
    void refresh();
  }

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const selectedNeedingReview = selectedRows.filter((r) => r.needsReview).map((r) => r.id);

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-3xl text-cream-50">Setup</h1>
          <p className="mt-1 max-w-2xl text-sm text-cream-300/70">
            The lists behind the wine form. Members add a producer, region, vineyard or grape when it's missing — check
            the spelling here, merge duplicates, and add what the club needs.
          </p>
        </div>
        <Button variant="quiet" className="px-2 text-xs" loading={seed.isPending} onClick={() => seed.mutate()}>
          Add missing defaults
        </Button>
      </header>

      <nav className="flex flex-wrap gap-1 rounded-lg border border-ink-800 p-1" aria-label="Lists">
        {WINE_LOOKUP_KINDS.map((k, i) => {
          const pending = reviewCounts[i]?.data?.meta.total ?? 0;
          return (
            <button
              key={k}
              type="button"
              onClick={() => switchKind(k)}
              aria-current={kind === k ? 'page' : undefined}
              className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition ${
                kind === k ? 'bg-gold-600 text-on-gold' : 'text-cream-200 hover:text-gold-200'
              }`}
            >
              {WINE_LOOKUP_LABELS[k].many}
              {pending > 0 ? (
                <span
                  className={`rounded-full px-1.5 text-[10px] font-semibold ${
                    kind === k ? 'bg-ink-950/20' : 'bg-warning/20 text-warning'
                  }`}
                  aria-label={`${pending} awaiting review`}
                >
                  {pending}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      <div className="flex flex-wrap items-center gap-3">
        <input
          className="field min-w-0 flex-1 basis-56"
          type="search"
          placeholder={`Search ${many}…`}
          aria-label={`Search ${many}`}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm text-cream-200">
          <input
            type="checkbox"
            className="h-4 w-4 accent-gold-500"
            checked={needsReview}
            onChange={(e) => {
              setNeedsReview(e.target.checked);
              setPage(1);
              setSelected(new Set());
            }}
          />
          Needs review only
        </label>
        <Button variant="ghost" className="shrink-0" onClick={() => setEditing('new')}>
          Add {one}
        </Button>
      </div>

      {listQuery.isLoading ? (
        <PageSkeleton lines={5} />
      ) : listQuery.isError ? (
        <p className="text-sm text-danger">{errorMessage(listQuery.error, `Could not load the ${many}`)}</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-ink-800 px-4 py-8 text-center">
          {needsReview && !debouncedQ ? (
            <>
              <p className="text-cream-100">Nothing waiting for review.</p>
              <p className="mt-1 text-sm text-cream-300/60">Everything members added to {many} has been checked.</p>
              <Button variant="ghost" className="mt-4" onClick={() => setNeedsReview(false)}>
                Show all {many}
              </Button>
            </>
          ) : (
            <p className="text-sm text-cream-300/60">
              No {many} match{debouncedQ ? ` “${debouncedQ}”` : ''}.
              {needsReview ? (
                <button type="button" className="ml-2 text-gold-300 underline" onClick={() => setNeedsReview(false)}>
                  Search everything
                </button>
              ) : null}
            </p>
          )}
        </div>
      ) : (
        <section className="rounded-lg border border-ink-800" aria-label={labels.many}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-800 px-3 py-2 sm:px-4">
            <label className="flex cursor-pointer items-center gap-3 text-xs text-cream-300/70">
              <input
                type="checkbox"
                className="h-4 w-4 accent-gold-500"
                checked={allSelected}
                onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
              />
              {selected.size ? `${selected.size} selected` : 'Select all on this page'}
            </label>
            {selected.size ? (
              <div className="flex items-center gap-2">
                <Button
                  className="px-3 py-1 text-xs"
                  loading={review.isPending}
                  disabled={!selectedNeedingReview.length}
                  onClick={() => review.mutate(selectedNeedingReview)}
                >
                  Looks right{selectedNeedingReview.length ? ` (${selectedNeedingReview.length})` : ''}
                </Button>
                <Button variant="quiet" className="px-2 py-1 text-xs" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </div>
            ) : null}
          </div>
          <ul className="divide-y divide-ink-800">
            {rows.map((row) => (
              <LookupRow
                key={row.id}
                kind={kind}
                row={row}
                selected={selected.has(row.id)}
                onSelect={(on) =>
                  setSelected((s) => {
                    const next = new Set(s);
                    if (on) next.add(row.id);
                    else next.delete(row.id);
                    return next;
                  })
                }
                onRename={(name) => rename(row, name)}
                onReview={() => review.mutate([row.id])}
                onEdit={() => setEditing(row)}
                onMerge={(into) => setMerging({ row, into: into ?? null })}
                onDelete={() => void remove(row)}
              />
            ))}
          </ul>
        </section>
      )}

      {listQuery.data ? (
        <Pagination
          page={listQuery.data.meta.page}
          pageSize={listQuery.data.meta.pageSize}
          total={listQuery.data.meta.total}
          onPage={setPage}
        />
      ) : null}

      <LookupEditDialog
        open={editing !== null}
        kind={kind}
        row={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSubmit={saveDraft}
        onMergeInstead={(existing) => {
          if (editing && editing !== 'new') setMerging({ row: editing, into: existing });
          setEditing(null);
        }}
      />
      <MergeDialog
        kind={kind}
        row={merging?.row ?? null}
        initialInto={merging?.into ?? null}
        onClose={() => setMerging(null)}
        onMerged={() => {
          setSelected(new Set());
          void refresh();
        }}
      />
    </div>
  );
}
